import fetcher from "../../../lib/fetcher";

// alo WiFi web store support endpoints, served by the management API like the rest of the dashboard.

export class AloApiError extends Error {
  constructor(status, code, message) {
    super(message || code || `Request failed (${status})`);
    this.name = "AloApiError";
    this.status = status;
    this.code = code;
  }
}

// Management API responses are wrapped as {success, code, message, body}, like the other pages read them.
function unwrap(response) {
  if (response && typeof response === "object" && "success" in response) {
    if (response.success === false) {
      throw new AloApiError(
        Number(response.code) || 400,
        typeof response.error === "string" ? response.error : undefined,
        response.message
      );
    }
    return response.body;
  }
  return response;
}

async function call(url, data) {
  let response;
  try {
    response = await fetcher(url, data);
  } catch (error) {
    const status = error.response ? error.response.status : 0;
    const body = (error.response && error.response.data) || {};
    throw new AloApiError(
      status,
      typeof body.error === "string" && /^[A-Z_]+$/.test(body.error)
        ? body.error
        : status === 0
        ? "NETWORK"
        : `HTTP_${status}`,
      body.message
    );
  }
  return unwrap(response);
}

const query = (params) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") search.set(key, String(value));
  });
  const text = search.toString();
  return text ? `?${text}` : "";
};

export const aloApi = {
  summary: (from, to) => call(`web-store-orders/summary${query({ from, to })}`),
  listOrders: ({ from, to, status, q, page, size }) =>
    call(`web-store-orders${query({ from, to, status, q, page, size })}`),
  getOrder: (orderId) => call(`web-store-orders/${orderId}`),
  resend: (orderId, channel, to) =>
    call(`web-store-orders/${orderId}/resend`, to ? { channel, to } : { channel }),
  // POST with an empty body: fetcher() switches to POST only when there is a body.
  retryVoucher: (orderId) => call(`web-store-orders/${orderId}/retry-voucher`, {}),
};

const MESSAGES = {
  NETWORK: "Can't reach the management API. Check your connection and try again.",
  FORBIDDEN: "You don't have permission for this. Ask an admin for order:read (view) or order:update (actions).",
  ORDER_NOT_FOUND: "This order wasn't found.",
  VOUCHER_NOT_ISSUED: "This order has no alo code yet, so there is nothing to resend.",
  NO_EMAIL: "The customer didn't give an email. Type one in to send the code.",
  INVALID_PHONE: "Enter a valid UAE mobile number, for example 050 123 4567.",
  INVALID_EMAIL: "Enter a valid email address.",
  NOT_PAID: "This order isn't paid, so there is no code to issue.",
  ALREADY_DELIVERED: "The code was already issued for this order.",
  VOUCHER_IN_PROGRESS: "The code is being issued right now. Give it a few minutes, then refresh.",
  NO_VOUCHER_ORDER: "This order has no Giftery voucher to retry. Check the order history and refund if needed.",
};

/** True when the management API doesn't have the web store endpoints yet (not deployed). */
export const isApiMissing = (error) =>
  error instanceof AloApiError && error.status === 404 && error.code !== "ORDER_NOT_FOUND";

export function describeError(error) {
  if (!(error instanceof AloApiError)) return "Something went wrong. Please try again.";
  if (isApiMissing(error)) return "The alo store endpoints aren't available on the management API yet.";
  if (error.status === 401) return "Your session has expired. Refresh it or log in again.";
  if (error.status === 403) return MESSAGES.FORBIDDEN;
  return MESSAGES[error.code] || error.message || "Something went wrong. Please try again.";
}
